import { AttendanceRuleWithNameAndEventType } from 'types';
import {
  computeTotalEstimatedSessions,
  computeTotalValidity,
  formatTimePeriod,
} from 'utils/billing.helpers';

import { isLimitedAttendanceRule } from '../formValidators';
import { AttendanceRuleFormValue } from '../types';

const formatRenewalSummary = (
  packageLength: string,
  limitedCredits: number | null,
  autoRenews: boolean,
): string => {
  const outcome = autoRenews ? 'Renews' : 'Ends';
  if (!limitedCredits) {
    return `${outcome} after ${packageLength}. Add a session limit to set the credit condition.`;
  }
  const creditCondition =
    limitedCredits === 1 ? 'once its 1 credit is used' : `once all ${limitedCredits} credits are used`;
  return `${outcome} after ${packageLength}, or ${creditCondition}, whichever comes first.`;
};

interface IRenewalSummaryInput {
  attendanceRules: AttendanceRuleFormValue[];
  lengthAmount: number | null;
  lengthPeriod: string | null;
  autoRenews: boolean;
}

/**
 * The plain-language end condition of a time-or-credit-based package: when it renews, or when it
 * ends if it does not auto-renew. Null until the package has a length. Credits pool the limited
 * rules only.
 */
export const buildRenewalSummary = ({
  attendanceRules,
  lengthAmount,
  lengthPeriod,
  autoRenews,
}: IRenewalSummaryInput): string | null => {
  const packageLength = formatTimePeriod(lengthAmount, lengthPeriod);
  if (!packageLength) return null;

  const limitedCredits = computeTotalEstimatedSessions(
    attendanceRules.filter(isLimitedAttendanceRule),
    'TIME_OR_CREDIT_BASED',
  );
  return formatRenewalSummary(packageLength, limitedCredits, autoRenews);
};

/**
 * The end condition of a saved time-or-credit-based package, from its attendance rules. The time
 * figure is the package length the builder shows: the longest validity plus delay, in the rules'
 * shared interval. Null when the rules have no validity interval.
 */
export const buildRenewalSummaryFromRules = (
  attendanceRules: AttendanceRuleWithNameAndEventType[],
  autoRenews: boolean,
): string | null => {
  const packageLength = formatTimePeriod(
    computeTotalValidity(attendanceRules),
    attendanceRules[0]?.validityInterval ?? null,
  );
  if (!packageLength) return null;

  const limitedCredits = computeTotalEstimatedSessions(
    attendanceRules.filter(rule => rule.credits !== null && rule.credits > 0),
    'TIME_OR_CREDIT_BASED',
  );
  return formatRenewalSummary(packageLength, limitedCredits, autoRenews);
};
